// Superadmin: the platform's model catalogue — which LLM and embedding models
// tenants may use, and what each costs. It lives in the control database (it
// used to be a dict in the runtime package), so adding a model or changing a
// price no longer needs a release.
//
// Models are discarded, not deleted: a discarded model disappears from tenant
// pickers and agents still pointed at it are refused with a "discarded" error,
// until the tenant repoints them or the model is restored here.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import * as React from "react";

import { PageHeader } from "@/components/layout-bits";
import {
  DiscardModelDialog,
  EmbeddingModelDialog,
  LlmModelDialog,
} from "@/components/models/platform-model-dialogs";
import {
  Badge,
  Button,
  Card,
  Input,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toaster";
import { restorePlatformModel } from "@/lib/api/endpoints";
import { usePlatformEmbeddingModels, usePlatformLlmModels } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import { cn, formatDate } from "@/lib/utils";
import type {
  CatalogModelStatus,
  PlatformEmbeddingModel,
  PlatformLlmModel,
  PlatformModelKind,
} from "@/types/api";

const usd = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;

const tokens = (n: number) =>
  n >= 1_000_000
    ? `${Number((n / 1_000_000).toFixed(2))}M`
    : n >= 1_000
      ? `${Math.round(n / 1_000)}k`
      : String(n);

type Editing =
  | { kind: "llm"; row: PlatformLlmModel | null }
  | { kind: "embedding"; row: PlatformEmbeddingModel | null }
  | null;

export default function ModelCatalogPage() {
  const qc = useQueryClient();
  const [tab, setTab] = React.useState<PlatformModelKind>("llm");
  const [q, setQ] = React.useState("");
  const [editing, setEditing] = React.useState<Editing>(null);
  const [discarding, setDiscarding] = React.useState<{
    kind: PlatformModelKind;
    modelId: string;
  } | null>(null);

  const llm = usePlatformLlmModels();
  const embedding = usePlatformEmbeddingModels();

  const restore = useMutation({
    mutationFn: (v: { kind: PlatformModelKind; modelId: string }) =>
      restorePlatformModel(v.kind, v.modelId),
    onSuccess: (_data, v) => {
      toast.success(`${v.modelId} restored — tenants can select it again.`);
      void qc.invalidateQueries({ queryKey: qk.platformModels(v.kind) });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const needle = q.trim().toLowerCase();
  const matches = (m: { model_id: string; provider: string; status: string }) =>
    !needle ||
    m.model_id.toLowerCase().includes(needle) ||
    m.provider.toLowerCase().includes(needle) ||
    m.status.includes(needle);

  const actions = (kind: PlatformModelKind, modelId: string, status: CatalogModelStatus) =>
    status === "discarded" ? (
      <Button
        size="sm"
        variant="outline"
        disabled={restore.isPending}
        onClick={() => restore.mutate({ kind, modelId })}
      >
        Restore
      </Button>
    ) : (
      <Button size="sm" variant="ghost" onClick={() => setDiscarding({ kind, modelId })}>
        Discard
      </Button>
    );

  return (
    <div>
      <PageHeader
        title="Model catalog"
        description="The models tenants can run their agents and knowledge bases on, and what each costs. Changes take effect across the platform within about 30 seconds."
        actions={
          <Button onClick={() => setEditing({ kind: tab, row: null } as Editing)}>
            <Plus className="h-4 w-4" /> Add {tab === "llm" ? "LLM" : "embedding"} model
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as PlatformModelKind)}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="llm">LLM models</TabsTrigger>
            <TabsTrigger value="embedding">Embedding models</TabsTrigger>
          </TabsList>
          <div className="w-full max-w-xs">
            <Input
              placeholder="Search models…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search models"
            />
          </div>
        </div>

        <TabsContent value="llm">
          <CatalogCard
            query={llm}
            rows={(llm.data ?? []).filter(matches)}
            searching={!!needle}
            onAdd={() => setEditing({ kind: "llm", row: null })}
            head={
              <TR>
                <TH>Model</TH>
                <TH className="text-right">Input / 1M</TH>
                <TH className="text-right">Output / 1M</TH>
                <TH className="text-right">Context</TH>
                <TH>Capabilities</TH>
                <TH>Status</TH>
                <TH />
              </TR>
            }
            renderRow={(m: PlatformLlmModel) => (
              <TR key={m.id} className={cn(m.status === "discarded" && "opacity-70")}>
                <ModelCell id={m.model_id} provider={m.provider} />
                <TD className="text-right font-mono text-xs tabular-nums">{usd(m.input_per_1m)}</TD>
                <TD className="text-right font-mono text-xs tabular-nums">
                  {usd(m.output_per_1m)}
                </TD>
                <TD className="text-right text-sm tabular-nums">{tokens(m.context_window)}</TD>
                <TD>
                  <div className="flex flex-wrap gap-1">
                    {m.supports_tools ? <Badge variant="outline">tools</Badge> : null}
                    {m.supports_streaming ? <Badge variant="outline">streaming</Badge> : null}
                    {m.supports_json_mode ? <Badge variant="outline">json</Badge> : null}
                  </div>
                </TD>
                <StatusCell status={m.status} reason={m.discard_reason} at={m.discarded_at} />
                <TD className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing({ kind: "llm", row: m })}
                    >
                      Edit
                    </Button>
                    {actions("llm", m.model_id, m.status)}
                  </div>
                </TD>
              </TR>
            )}
          />
        </TabsContent>

        <TabsContent value="embedding">
          <CatalogCard
            query={embedding}
            rows={(embedding.data ?? []).filter(matches)}
            searching={!!needle}
            onAdd={() => setEditing({ kind: "embedding", row: null })}
            head={
              <TR>
                <TH>Model</TH>
                <TH className="text-right">Dimensions</TH>
                <TH className="text-right">Price / 1M</TH>
                <TH>Status</TH>
                <TH />
              </TR>
            }
            renderRow={(m: PlatformEmbeddingModel) => (
              <TR key={m.id} className={cn(m.status === "discarded" && "opacity-70")}>
                <ModelCell id={m.model_id} provider={m.provider} />
                <TD className="text-right text-sm tabular-nums">{m.dimensions ?? "—"}</TD>
                <TD className="text-right font-mono text-xs tabular-nums">{usd(m.price_per_1m)}</TD>
                <StatusCell status={m.status} reason={m.discard_reason} at={m.discarded_at} />
                <TD className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing({ kind: "embedding", row: m })}
                    >
                      Edit
                    </Button>
                    {actions("embedding", m.model_id, m.status)}
                  </div>
                </TD>
              </TR>
            )}
          />
        </TabsContent>
      </Tabs>

      <LlmModelDialog
        open={editing?.kind === "llm"}
        onOpenChange={(open) => !open && setEditing(null)}
        row={editing?.kind === "llm" ? editing.row : null}
      />
      <EmbeddingModelDialog
        open={editing?.kind === "embedding"}
        onOpenChange={(open) => !open && setEditing(null)}
        row={editing?.kind === "embedding" ? editing.row : null}
      />
      <DiscardModelDialog target={discarding} onClose={() => setDiscarding(null)} />
    </div>
  );
}

function CatalogCard<T>({
  query,
  rows,
  searching,
  onAdd,
  head,
  renderRow,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  rows: T[];
  searching: boolean;
  onAdd: () => void;
  head: React.ReactNode;
  renderRow: (row: T) => React.ReactNode;
}) {
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return (
    <Card className="p-4">
      {query.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          title={searching ? "No models match your search" : "No models in the catalogue"}
          hint={searching ? undefined : "Add one and tenants can select it straight away."}
          action={searching ? undefined : <Button onClick={onAdd}>Add model</Button>}
        />
      ) : (
        <Table>
          <THead>{head}</THead>
          <TBody>{rows.map(renderRow)}</TBody>
        </Table>
      )}
    </Card>
  );
}

function ModelCell({ id, provider }: { id: string; provider: string }) {
  return (
    <TD>
      <div className="font-mono text-sm">{id}</div>
      <div className="text-xs text-muted-foreground">{provider}</div>
    </TD>
  );
}

function StatusCell({
  status,
  reason,
  at,
}: {
  status: CatalogModelStatus;
  reason: string | null;
  at: string | null;
}) {
  if (status === "active") {
    return (
      <TD>
        <Badge variant="success">active</Badge>
      </TD>
    );
  }
  return (
    <TD>
      <Badge variant="destructive">discarded</Badge>
      <div className="mt-1 max-w-[16rem] text-xs text-muted-foreground">
        {formatDate(at)}
        {reason ? ` — ${reason}` : ""}
      </div>
    </TD>
  );
}
